import { useMemo } from 'react';
import { Alert } from 'react-bootstrap';
import {
  countUrlListDuplicates,
  listUrlListDuplicateLines,
  parseUrlList,
  partitionUrlsForProjectDomain,
  projectUrlListPlaceholder,
  projectUrlPlaceholder,
  resolveProjectHost,
  type ProjectUrlHints,
} from '../lib/utils';
import { UrlListTextarea } from './UrlListTextarea';

export function UrlListField({
  project,
  value,
  onChange,
  limit,
  singleLinePlaceholder,
}: {
  project: ProjectUrlHints;
  value: string;
  onChange: (value: string) => void;
  limit: number;
  singleLinePlaceholder?: boolean;
}) {
  const urls = useMemo(() => parseUrlList(value), [value]);
  const duplicateCount = useMemo(() => countUrlListDuplicates(value), [value]);
  const duplicateLines = useMemo(() => listUrlListDuplicateLines(value), [value]);
  const projectHost = resolveProjectHost(project);
  const { invalid, wrongDomain } = useMemo(
    () => partitionUrlsForProjectDomain(urls, project),
    [urls, project],
  );

  const placeholder = singleLinePlaceholder
    ? projectUrlPlaceholder(project)
    : projectUrlListPlaceholder(project);

  return (
    <>
      <UrlListTextarea value={value} placeholder={placeholder} onChange={onChange} />
      <div className="d-flex flex-wrap align-items-center gap-2 text-secondary small mt-2">
        <span>{urls.length} آدرس یکتا</span>
        <span className="text-secondary opacity-50" aria-hidden>
          |
        </span>
        {projectHost ? (
          <span>
            دامنه مجاز: <span className="mono">{projectHost}</span>
          </span>
        ) : (
          <span className="text-warning">دامنه پروژه تنظیم نشده است</span>
        )}
      </div>
      {duplicateCount > 0 && (
        <Alert variant="danger" className="border-0 py-2 small mt-2 mb-0">
          {duplicateCount} آدرس تکراری
          {duplicateLines.length > 0 && (
            <>
              : {duplicateLines.slice(0, 3).join('، ')}
              {duplicateLines.length > 3
                ? ` و ${duplicateLines.length - 3} مورد دیگر`
                : ''}
            </>
          )}
        </Alert>
      )}
      {urls.length > limit && (
        <Alert variant="danger" className="border-0 py-2 small mt-2 mb-0">
          {urls.length} آدرس وارد شده — حداکثر {limit} آدرس در هر درخواست مجاز
          است.
        </Alert>
      )}
      {invalid.length > 0 && (
        <Alert variant="warning" className="border-0 py-2 small mt-2 mb-0">
          آدرس نامعتبر: {invalid.slice(0, 3).join('، ')}
          {invalid.length > 3 ? ` و ${invalid.length - 3} مورد دیگر` : ''}
        </Alert>
      )}
      {wrongDomain.length > 0 && projectHost && (
        <Alert variant="warning" className="border-0 py-2 small mt-2 mb-0">
          این آدرس‌ها متعلق به دامنه {projectHost} نیستند:{' '}
          {wrongDomain.slice(0, 3).join('، ')}
          {wrongDomain.length > 3 ? ` و ${wrongDomain.length - 3} مورد دیگر` : ''}
        </Alert>
      )}
    </>
  );
}
